import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import SaveIcon from "@mui/icons-material/Save";
import { hubApi } from "../services/hubApi";

export default function SIPConfig({ connectionId, mode = "voip" }) {
  const [credentials, setCredentials] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(null);

  // SIP connect form (for new SIP trunk connections)
  const [form, setForm] = useState({
    name: "",
    sipHost: "",
    sipUser: "",
    sipPassword: "",
    sipPort: 5060,
    sipTransport: "udp",
    callerIdNumber: "",
    callerIdName: "",
  });
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [connectSuccess, setConnectSuccess] = useState(false);

  const isNewConnection = !connectionId && mode === "sip";

  const loadCredentials = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await hubApi.voipGetSIPCredentials(connectionId, revealed);
      setCredentials(data.credentials || {});
    } catch (err) {
      console.error("Error loading SIP credentials:", err);
    } finally {
      setLoading(false);
    }
  }, [connectionId, revealed]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const handleTest = async () => {
    if (!connectionId) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await hubApi.sipTest(connectionId);
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.error || "Erro" });
    } finally {
      setTesting(false);
    }
  };

  const handleCopy = (value, key) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleToggleReveal = () => setRevealed(!revealed);

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setConnectError(null);
    setConnectSuccess(false);
  };

  const handleConnect = async () => {
    if (!form.sipHost || !form.sipUser || !form.sipPassword) {
      setConnectError("Host, usuario e senha SIP sao obrigatorios");
      return;
    }
    setConnecting(true);
    setConnectError(null);
    try {
      await hubApi.sipConnect(form);
      setConnectSuccess(true);
    } catch (err) {
      setConnectError(err.response?.data?.error || "Erro ao conectar SIP");
    } finally {
      setConnecting(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#111827",
      borderRadius: "10px",
      "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    },
    "& .MuiInputLabel-root": { color: "#6B7280" },
    "& .MuiOutlinedInput-input": { color: "#F3F4F6", fontSize: "0.85rem" },
  };

  // ── New SIP connection form ──
  if (isNewConnection) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#F3F4F6", mb: 3 }}>
          Conectar SIP Trunk
        </Typography>

        {connectError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {connectError}
          </Alert>
        )}
        {connectSuccess && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            SIP Trunk conectado com sucesso!
          </Alert>
        )}

        <Card
          sx={{
            bgcolor: "#1E293B",
            border: "1px solid rgba(255,255,255,0.04)",
            borderRadius: "14px",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nome da conexao"
                  value={form.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  placeholder="Ex: Linha Vivo Comercial"
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Host SIP *"
                  value={form.sipHost}
                  onChange={(e) => handleFormChange("sipHost", e.target.value)}
                  placeholder="sip.operadora.com.br"
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Porta"
                  type="number"
                  value={form.sipPort}
                  onChange={(e) =>
                    handleFormChange("sipPort", parseInt(e.target.value) || 5060)
                  }
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Usuario SIP *"
                  value={form.sipUser}
                  onChange={(e) => handleFormChange("sipUser", e.target.value)}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Senha SIP *"
                  type="password"
                  value={form.sipPassword}
                  onChange={(e) => handleFormChange("sipPassword", e.target.value)}
                  sx={fieldSx}
                  autoComplete="new-password"
                  inputProps={{ autoComplete: "new-password" }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>Transporte</InputLabel>
                  <Select
                    value={form.sipTransport}
                    label="Transporte"
                    onChange={(e) =>
                      handleFormChange("sipTransport", e.target.value)
                    }
                  >
                    <MenuItem value="udp">UDP</MenuItem>
                    <MenuItem value="tcp">TCP</MenuItem>
                    <MenuItem value="tls">TLS</MenuItem>
                    <MenuItem value="wss">WSS (WebSocket)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Caller ID (numero)"
                  value={form.callerIdNumber}
                  onChange={(e) =>
                    handleFormChange("callerIdNumber", e.target.value)
                  }
                  placeholder="+5511999999999"
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Caller ID (nome)"
                  value={form.callerIdName}
                  onChange={(e) =>
                    handleFormChange("callerIdName", e.target.value)
                  }
                  placeholder="OtimizAPP"
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleConnect}
                disabled={connecting}
                startIcon={
                  connecting ? <CircularProgress size={16} /> : <SaveIcon />
                }
                sx={{
                  bgcolor: "#06B6D4",
                  "&:hover": { bgcolor: "#0891B2" },
                  borderRadius: "10px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                {connecting ? "Conectando..." : "Conectar SIP Trunk"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Existing connection credentials view ──
  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: "#8B5CF6" }} />
      </Box>
    );
  }

  const credentialFields = [
    { label: "Host", value: credentials?.host, key: "host" },
    { label: "Usuario", value: credentials?.user, key: "user" },
    {
      label: "Senha",
      value: revealed ? credentials?.password : credentials?.passwordMasked || "********",
      key: "password",
      secret: true,
    },
    { label: "Porta", value: credentials?.port, key: "port" },
    { label: "Transporte", value: credentials?.transport, key: "transport" },
    {
      label: "WebSocket URL",
      value: credentials?.websocketUrl,
      key: "websocketUrl",
    },
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#F3F4F6", mb: 3 }}>
        Configuracao SIP
      </Typography>

      <Card
        sx={{
          bgcolor: "#1E293B",
          border: "1px solid rgba(255,255,255,0.04)",
          borderRadius: "14px",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#9CA3AF",
              mb: 2,
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.7rem",
              letterSpacing: "0.5px",
            }}
          >
            Credenciais SIP
          </Typography>

          <Grid container spacing={2}>
            {credentialFields.map((field) => (
              <Grid item xs={12} sm={6} key={field.key}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  value={field.value || ""}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {field.secret && (
                          <IconButton
                            size="small"
                            onClick={handleToggleReveal}
                            sx={{ color: "#6B7280" }}
                          >
                            {revealed ? (
                              <VisibilityOffIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        )}
                        <Tooltip
                          title={
                            copied === field.key ? "Copiado!" : "Copiar"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleCopy(String(field.value || ""), field.key)
                            }
                            sx={{ color: "#6B7280" }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", my: 2.5 }} />

          {/* Test connection */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={testing}
              startIcon={
                testing ? (
                  <CircularProgress size={16} />
                ) : (
                  <NetworkCheckIcon />
                )
              }
              sx={{
                borderColor: "rgba(255,255,255,0.15)",
                color: "#D1D5DB",
                "&:hover": {
                  borderColor: "#8B5CF6",
                  color: "#8B5CF6",
                },
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {testing ? "Testando..." : "Testar Conexao"}
            </Button>

            {testResult && (
              <Chip
                icon={
                  testResult.success ? (
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ErrorIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={
                  testResult.success
                    ? `OK (${testResult.latency}ms)`
                    : testResult.error || "Falhou"
                }
                size="small"
                sx={{
                  bgcolor: testResult.success
                    ? "rgba(37,211,102,0.12)"
                    : "rgba(239,68,68,0.12)",
                  color: testResult.success ? "#25D366" : "#EF4444",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  "& .MuiChip-icon": {
                    color: testResult.success ? "#25D366" : "#EF4444",
                  },
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
